import React from 'react';
import dynamic from 'next/dynamic';
import cn from 'classnames';
// Hooks
import { useAppContext, useOnboardingContext } from 'hooks';
// Watchers
import GoogleSdkWatcher from 'layouts/Header/Watchers/GoogleSdkWatcher';
import FacebookSdkWatcher from 'layouts/Header/Watchers/FacebookSdkWatcher';
import TwitterSdkWatcher from 'layouts/Header/Watchers/TwitterSdkWatcher';
import TiktokSdkWatcher from 'layouts/Header/Watchers/TiktokSdkWatcher';
import ShoppingCartWatcher from 'layouts/Header/Watchers/ShoppingCartWatcher';
import SyncBrowserTabsWatcher from 'layouts/Header/Watchers/SyncBrowserTabsWatcher';
import OnboardingWatcher from './Watchers/OnboardingWatcher';
import OnboardingV2Watcher from './Watchers/OnboardingV2Watcher';
import PpvBuyPostAuthWatcher from './Watchers/PpvBuyPostAuthWatcher';
import CookieTokenWatcher from './Watchers/CookieTokenWatcher';
import BrandDealInviteWatcher from './Watchers/BrandDealInviteWatcher';
import MatchupInviteWatcher from './Watchers/MatchupInviteWatcher';
import AuthModalQueryWatcher from './Watchers/AuthModalQueryWatcher';
// Components
import DesktopHeader from 'layouts/Header/DesktopHeader/DesktopHeader';
import MobileHeader from 'layouts/Header/MobileHeader/MobileHeader';
import Onboarding from 'components/Onboarding/Onboarding';
import CreatorSetup from 'components/CreatorSetup/CreatorSetup';
import CreateStreamSetup from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Styles
import styles from './Header.module.scss';
// Dynamic imports
const SignIn = dynamic(() => import('components/SignIn/SignIn/SignIn'), {
  ssr: false,
});
const SignUp = dynamic(() => import('components/SignUp/SignUpV2/SignUpV2'), {
  ssr: false,
});
const ForgotPassword = dynamic(
  () =>
    import(
      'components/ForgotPassword/ForgotPasswordContainer/ForgotPasswordContainer'
    ),
  {
    ssr: false,
  }
);
const ResetPassword = dynamic(
  () =>
    import(
      'components/ResetPassword/ResetPasswordContainer/ResetPasswordContainer'
    ),
  {
    ssr: false,
  }
);

export type Theme = 'black' | 'white';

type Props = {
  transparent?: boolean;
  fixed?: boolean;
  hidden?: boolean;
};

const Header: React.FC<Props> = ({
  transparent,
  fixed = true,
  hidden = false,
}) => {
  // Gate the heavy auth modals on their visibility flags so anonymous
  // viewers don't download SignIn / SignUp / ForgotPassword / ResetPassword
  // chunks until the user (or a URL deep-link via `AuthModalQueryWatcher`)
  // actually opens one. Each `dynamic({ ssr: false })` import only kicks off
  // when the JSX is mounted.
  const {
    showLoginModal,
    showSignUpModal,
    showForgotPasswordModal,
    showResetPasswordModal,
  } = useAppContext();
  const { showCreateStreamModal } = useOnboardingContext();

  return (
    <>
      <header
        className={cn({
          [styles.hidden]: hidden,
        })}
      >
        <DesktopHeader fixed={fixed} />
        <MobileHeader transparent={transparent} fixed={fixed} />

        <AuthModalQueryWatcher />
        {showLoginModal && <SignIn />}
        {showSignUpModal && <SignUp />}
        {showForgotPasswordModal && <ForgotPassword />}
        {showResetPasswordModal && <ResetPassword />}
        <Onboarding />
        <CreatorSetup />
        {showCreateStreamModal && <CreateStreamSetup />}
        <OnboardingWatcher />
        <OnboardingV2Watcher />
        <PpvBuyPostAuthWatcher />
        <GoogleSdkWatcher />
        <FacebookSdkWatcher />
        <TwitterSdkWatcher />
        <TiktokSdkWatcher />
        <ShoppingCartWatcher />
        <SyncBrowserTabsWatcher />
        <CookieTokenWatcher />
        <BrandDealInviteWatcher />
        <MatchupInviteWatcher />
      </header>
    </>
  );
};

export default Header;
